/**
 * AgentAllocationMatrix Component
 * AIDLC Administrative Backend
 *
 * Cross-project agent allocation table showing which agents are on which projects,
 * with each agent's current status.
 *
 * Row shape follows GET /api/projects/allocation-matrix: `assignments`, `role`
 * and `status` (the server sends no utilization figure).
 */

import { useState, useEffect } from 'react';
import { fetchAllocationMatrix } from '../../../services/projectManagementService';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import type { AllocationEntry } from '../../../types/projects';

export function AgentAllocationMatrix() {
  const [entries, setEntries] = useState<AllocationEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchAllocationMatrix();
        if (!cancelled) setEntries(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load allocation data');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <LoadingSpinner message="Loading allocation matrix..." size="sm" />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-red-700 text-xs">{error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        title="No agent allocations"
        description="Assign agents to projects to see the allocation matrix."
      />
    );
  }

  // Collect all unique project names for column headers
  const allProjects = new Map<string, string>();
  entries.forEach((e) => {
    (e.assignments ?? []).forEach((p) => {
      allProjects.set(p.projectId, p.projectName);
    });
  });
  const projectList = Array.from(allProjects.entries());

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">
                Agent
              </th>
              {projectList.map(([pid, pname]) => (
                <th key={pid} className="text-center px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">
                  {pname}
                </th>
              ))}
              <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase min-w-[100px]">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {entries.map((entry) => {
              const assignedProjectIds = new Set((entry.assignments ?? []).map((p) => p.projectId));
              return (
                <tr key={entry.agentId} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-xs font-medium text-gray-800 sticky left-0 bg-white dark:bg-zinc-900 z-10">
                    {entry.agentName}
                    {entry.role && <span className="block text-[10px] font-normal text-gray-500">{entry.role}</span>}
                  </td>
                  {projectList.map(([pid]) => (
                    <td key={pid} className="px-4 py-2.5 text-center">
                      {assignedProjectIds.has(pid) ? (
                        <span className="inline-block w-5 h-2 bg-ldaja-blue rounded-sm" title="Assigned" />
                      ) : (
                        <span className="text-gray-200">--</span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-gray-600 capitalize">{entry.status || 'unknown'}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
